import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Sparkles, 
  ArrowRight, 
  ShieldCheck, 
  Package, 
  CheckCircle, 
  PhoneCall, 
  Layers, 
  ShoppingBag,
  TrendingUp,
  Percent,
  Clock
} from "lucide-react";
import apiClient, { resolveImageUrl } from "@/utils/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const { settings } = useSiteSettings();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredPin, setFeaturedPin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          apiClient.get("/products"),
          apiClient.get("/categories")
        ]);
        setFeaturedProducts(prodRes.data || []);
        setCategories(catRes.data || []);
      } catch (err) {
        console.error("Home data fetch error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Featured This Week pin — admin picks a product ID in Settings
  useEffect(() => {
    const pinId = settings.featured_this_week_product_id;
    if (!pinId) { setFeaturedPin(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/products/${pinId}`);
        if (!cancelled) setFeaturedPin(data?.product || null);
      } catch (err) {
        if (!cancelled) setFeaturedPin(null);
      }
    })();
    return () => { cancelled = true; };
  }, [settings.featured_this_week_product_id]);

  const heroSlides = [settings.hero_image_1, settings.hero_image_2].filter(Boolean);
  const heroStoryCaptions = [
    settings.hero_story_caption_1 || "",
    settings.hero_story_caption_2 || "",
  ];
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    if (heroSlides.length < 2) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  const activeStoryCaption = heroStoryCaptions[heroIdx] || "";
  const heroEyebrow = settings.hero_story_eyebrow || "Original China Imported Laces";

  return (
    <div className="space-y-16 sm:space-y-24 pb-16">
      
      {/* 1. HERO BANNER — 2-slide slider (images uploaded by admin) */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[580px] sm:min-h-[640px] flex items-center bg-[#1C1917] overflow-hidden border-b-2 border-[#D4AF37]"
      >
        {/* Background Image with Gold Tint Overlay */}
        <div className="absolute inset-0 z-0">
          {heroSlides.length > 0 ? (
            heroSlides.map((src, i) => (
              <img
                key={i}
                src={resolveImageUrl(src)}
                alt=""
                data-testid={`hero-slide-${i}`}
                className={`absolute inset-0 w-full h-full object-cover object-center opacity-30 scale-105 filter saturate-150 transition-opacity duration-1000 ${
                  i === heroIdx ? "opacity-30" : "opacity-0"
                }`}
              />
            ))
          ) : (
            <div className="absolute inset-0 bg-[#1C1917]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917] via-[#1C1917]/85 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(#D4AF37_1px,transparent_1px)] [background-size:24px_24px] opacity-10" />
        </div>

        {/* Slide indicators */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setHeroIdx(i)}
                data-testid={`hero-slide-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === heroIdx ? "w-8 bg-[#D4AF37]" : "w-3 bg-white/40"
                }`}
              />
            ))}
          </div>
        )}

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 max-w-2xl space-y-6">

              {/* Tagline Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FAF8F5]/10 border border-[#D4AF37]/60 text-[#E6C687] text-xs font-semibold tracking-wider uppercase backdrop-blur-md">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span data-testid="hero-eyebrow">{heroEyebrow}</span>
              </div>

              {/* Main Headline */}
              <h1
                data-testid="hero-main-heading"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.15] tracking-tight"
              >
                Elevate Bridal & Formal Couture with <span className="text-gold-gradient font-serif italic">Pure Elegance</span>
              </h1>

              {/* Subheading / Description */}
              <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
                Discover Pakistan's premier collection of original China-imported diamond sitara laces, zari sitara borders, organza cutwork and complete lace sets for wholesale &amp; retail.
              </p>

              {/* Story caption reel — changes with the hero slide */}
              {activeStoryCaption && (
                <div
                  key={heroIdx}
                  data-testid={`hero-story-caption-${heroIdx}`}
                  className="flex items-center gap-3 pl-4 border-l-2 border-[#D4AF37] animate-fadeIn"
                >
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
                    Chapter {heroIdx + 1}
                  </span>
                  <span className="text-xs sm:text-sm text-white/85 font-serif italic leading-snug">
                    {activeStoryCaption}
                  </span>
                </div>
              )}

              {/* Hero CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-2">
                <Link
                  to="/shop"
                  data-testid="hero-explore-catalog-btn"
                  className="px-7 py-3.5 bg-gold-gradient hover:opacity-95 text-[#1C1917] text-sm font-bold rounded-xl shadow-luxury flex items-center justify-center gap-2 transition transform hover:-translate-y-0.5"
                >
                  <ShoppingBag className="w-4 h-4" />
                  <span>Explore Complete Sets</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/wholesale"
                  data-testid="hero-wholesale-portal-btn"
                  className="px-7 py-3.5 bg-[#FAF8F5]/10 hover:bg-[#FAF8F5]/20 text-white border border-[#E5E0D8]/40 hover:border-[#D4AF37] text-sm font-semibold rounded-xl backdrop-blur-md flex items-center justify-center gap-2 transition"
                >
                  <Package className="w-4 h-4 text-[#D4AF37]" />
                  <span>Wholesale & Bulk Orders</span>
                </Link>
              </div>

              {/* Quick Metrics Bar */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-stone-800/80 text-stone-300 text-xs">
                <div>
                  <span className="font-heading font-bold text-white text-lg sm:text-xl block text-[#E6C687]">10+</span>
                  <span className="text-stone-400">Specialized Categories</span>
                </div>
                <div>
                  <span className="font-heading font-bold text-white text-lg sm:text-xl block text-[#E6C687]">Complete</span>
                  <span className="text-stone-400">Set Packs Ready</span>
                </div>
                <div>
                  <span className="font-heading font-bold text-white text-lg sm:text-xl block text-[#E6C687]">Nationwide</span>
                  <span className="text-stone-400">Cash on Delivery</span>
                </div>
              </div>
            </div>

            {/* Featured This Week pin card — admin-editable */}
            {featuredPin && (
              <div className="lg:col-span-5 flex lg:justify-end">
                <Link
                  to={`/product/${featuredPin.id}`}
                  data-testid="hero-featured-this-week-pin"
                  className="group relative w-full max-w-sm bg-[#FAF8F5]/95 backdrop-blur-sm rounded-2xl border-2 border-[#D4AF37]/70 shadow-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300"
                >
                  {/* Pin ribbon */}
                  <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1C1917] text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider border border-[#D4AF37]/60">
                      <Sparkles className="w-3 h-3" />
                      <span data-testid="hero-featured-tagline">
                        {settings.featured_this_week_tagline || "Featured This Week"}
                      </span>
                    </span>
                    {settings.featured_this_week_subtitle && (
                      <span className="inline-block px-2 py-0.5 rounded-md bg-white/90 text-[10px] font-semibold text-[#996515] max-w-[220px]">
                        {settings.featured_this_week_subtitle}
                      </span>
                    )}
                  </div>

                  {/* Product image */}
                  <div className="aspect-[4/3] overflow-hidden bg-[#F5F2EB]">
                    {featuredPin.images?.[0] ? (
                      <img
                        src={resolveImageUrl(featuredPin.images[0])}
                        alt={featuredPin.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs italic">
                        No image yet
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div className="p-4 space-y-2 bg-white">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#996515]">
                      {featuredPin.category}
                    </span>
                    <h3 className="font-heading font-bold text-sm text-stone-900 leading-snug line-clamp-2">
                      {featuredPin.name}
                    </h3>
                    <div className="flex items-baseline justify-between pt-1">
                      <div>
                        <span className="text-[10px] text-stone-500">Retail</span>
                        <div className="font-heading font-extrabold text-lg text-stone-900 leading-none">
                          Rs. {Number(featuredPin.retail_price || 0).toLocaleString()}
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#996515] group-hover:text-[#1C1917] transition">
                        View Set
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            )}

          </div>
        </div>
      </section>

      {/* 2. CATEGORIES SHOWCASE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 pb-4 border-b border-[#E5E0D8]">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#996515]">
              Curated Collections
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1917] mt-1">
              Explore Our Signature Categories
            </h2>
          </div>
          <Link
            to="/categories"
            data-testid="view-all-categories-link"
            className="text-xs font-semibold text-[#996515] hover:text-[#1C1917] flex items-center gap-1 group"
          >
            <span>View All 10 Categories</span>
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
          {categories.map((cat) => (
            <Link
              key={cat.id || cat.slug}
              to={`/shop?category=${encodeURIComponent(cat.name)}`}
              data-testid={`category-card-${cat.slug}`}
              className="group relative bg-white rounded-xl overflow-hidden border border-[#E5E0D8] hover:border-[#D4AF37] transition duration-300 shadow-luxury hover:shadow-luxury-hover flex flex-col"
            >
              <div className="aspect-[4/3] sm:aspect-square overflow-hidden bg-[#FAF8F5]">
                <img
                  src={cat.image_url}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-3 text-white">
                <h3 className="font-heading font-semibold text-xs sm:text-sm group-hover:text-[#E6C687] transition line-clamp-2">
                  {cat.name}
                </h3>
                <span className="text-[10px] text-stone-300 flex items-center gap-1 mt-0.5">
                  <span>Browse Designs</span>
                  <ArrowRight className="w-2.5 h-2.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 3. FEATURED PRODUCTS (COMPLETE SETS) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#D4AF37]/15 text-[#996515] text-[11px] font-bold uppercase tracking-wider mb-2">
              <Sparkles className="w-3 h-3 text-[#D4AF37]" />
              Full Suit Sets
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1917]">
              Featured Complete Lace Sets
            </h2>
            <p className="text-xs sm:text-sm text-stone-600 mt-1">
              Pre-calculated Colors, Taan and Gaz yardage in every Complete Set pack.
            </p>
          </div>
          <Link
            to="/shop"
            data-testid="view-full-shop-link"
            className="px-4 py-2 rounded-lg bg-[#FAF8F5] border border-[#D4AF37] text-xs font-bold text-[#996515] hover:bg-[#D4AF37] hover:text-white transition flex items-center gap-1.5"
          >
            <span>Browse Full Catalog</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredProducts.slice(0, 8).map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {/* 4. WHOLESALE HERO SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          data-testid="wholesale-highlight-banner"
          className="relative bg-[#1C1917] rounded-2xl overflow-hidden border-2 border-[#D4AF37] p-8 sm:p-12 text-white shadow-2xl"
        >
          <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-20 hidden md:block">
            {settings.wholesale_section_image ? (
              <img
                src={resolveImageUrl(settings.wholesale_section_image)}
                alt=""
                data-testid="wholesale-section-image"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#2A2721]" />
            )}
          </div>

          <div className="relative z-10 max-w-xl space-y-5">
            <span className="px-3 py-1 bg-[#D4AF37] text-[#1C1917] text-xs font-bold tracking-widest uppercase rounded">
              Wholesale & Bulk Supply
            </span>
            
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
              Direct China Import Wholesale Rates for Boutiques & Retailers
            </h2>
            
            <p className="text-stone-300 text-sm leading-relaxed">
              Supplying fancy lace rolls, complete sets, diamond sitara trims, and organza cutwork in bulk to clothing brands, bridal studios, and market retailers across Lahore, Karachi, Faisalabad, and nationwide.
            </p>

            <div className="grid grid-cols-2 gap-4 py-2 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" />
                <span>Tiered Wholesale Discounts</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" />
                <span>Direct Factory Import Quality</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" />
                <span>Low Minimum Order Quantity</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" />
                <span>Fast Dispatch via Daewoo / TCS</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link
                to="/wholesale"
                data-testid="home-wholesale-btn"
                className="px-6 py-3 bg-[#D4AF37] hover:bg-[#E6C687] text-[#1C1917] font-bold text-xs rounded-xl text-center transition"
              >
                Submit Wholesale Inquiry
              </Link>
              <a
                href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Assalam%20o%20Alaikum,%20I%20am%20interested%20in%20wholesale%20rates%20for%20Anwer%20Fancy%20Lace.`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-wholesale-whatsapp-btn"
                className="px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 transition"
              >
                <PhoneCall className="w-3.5 h-3.5" />
                <span>WhatsApp Wholesale Desk</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 5. WHY ANWER FANCY LACE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#996515]">
            Our Hallmark of Quality
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1917] mt-1">
            Why Pakistan Trusts Anwer Fancy Lace
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-3">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 text-[#996515] flex items-center justify-center font-bold">
              <Sparkles className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-bold text-base text-stone-900">Original China Shine & Polish</h3>
            <p className="text-xs text-stone-600 leading-relaxed">
              Each sitara, zari strand, and crystal bead is crafted to resist tarnishing and sustain heavy wedding season stitching and dry-cleaning.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-3">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 text-[#996515] flex items-center justify-center font-bold">
              <Layers className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-bold text-base text-stone-900">Complete Set Guarantee</h3>
            <p className="text-xs text-stone-600 leading-relaxed">
              No mismatched shades or running short on yardage. Our complete sets provide pre-calculated gaz for dupatta, sleeves, daman, and trousers.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-3">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 text-[#996515] flex items-center justify-center font-bold">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-bold text-base text-stone-900">Multi-Channel Instant Ordering</h3>
            <p className="text-xs text-stone-600 leading-relaxed">
              Order seamlessly with Cash on Delivery, Bank Transfer, EasyPaisa, JazzCash, or 1-tap direct WhatsApp order dispatch.
            </p>
          </div>
        </div>
      </section>

      {/* 6. WHATSAPP FAST ORDER BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#FAF8F5] border-2 border-dashed border-[#D4AF37] rounded-2xl p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-2 text-center md:text-left">
            <span className="text-xs font-bold text-[#996515] uppercase tracking-wider">
              Instant Ordering & Assistance
            </span>
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-stone-900">
              Need lace sets right away or want live videos of samples?
            </h3>
            <p className="text-xs sm:text-sm text-stone-600">
              Send us a screenshot or voice note on WhatsApp. We reply within minutes during market hours.
            </p>
          </div>
          <a
            href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Assalam%20o%20Alaikum%20Anwer%20Fancy%20Lace,%20I%20want%20to%20place%20an%20order.`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-bottom-whatsapp-cta"
            className="px-8 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm rounded-xl flex items-center gap-2 shrink-0 shadow-md transition"
          >
            <PhoneCall className="w-4 h-4" />
            <span>Chat on WhatsApp Now</span>
          </a>
        </div>
      </section>

    </div>
  );
}
